import { Component, forwardRef, HostListener, Input, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator } from '@angular/forms';

@Component({
  selector: 'numeric-half-input',
  standalone: false,
  template: `
    <input
      [value]="displayValue"
      [disabled]="disabled"
      (input)="onInput($event)"
      (blur)="onBlur($event)"
      (focus)="onFocus($event)"
      type="text"
    />
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericHalfInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NumericHalfInputComponent),
      multi: true,
    },
  ],
})
export class NumericHalfInputComponent implements ControlValueAccessor, Validator {
  @Input() min = 0.5;
  @Input() max = 24;
  @Output() valueAccepted = new EventEmitter<number | null>();

  displayValue: string = '';
  modelValue: number | null = null;

  disabled = false;
  private onChange: any = () => {};
  private onTouched: any = () => {};

  /** Auto-select on focus */
  onFocus(event: any) {
    const input = event.target;
    setTimeout(() => input.select(), 0);
  }

  /** Main input handler */
  onInput(event: any) {
    if (this.disabled) return;

    const value = event.target.value;

    this.displayValue = value; // raw value stays

    // Normalize decimal input
    const normalized = value.replace(',', '.');

    // Try converting to number
    const n = Number(normalized);

    if (!isNaN(n)) {
      this.modelValue = n;
      this.onChange(n);
    } else {
      this.modelValue = null;
      this.onChange(null);
    }
  }

  /** On blur: cleanup, clamp & rounding */
  onBlur(event: any) {
    // If empty or 0, treat as null (valid)
    if (this.modelValue == null || this.modelValue === 0) {
      this.displayValue = '';
      this.modelValue = null;
      this.onChange(null);
      this.onTouched();
      this.valueAccepted.emit(null);
      return;
    }

    let v = this.modelValue;

    // Clamp to valid range
    v = Math.max(this.min, Math.min(this.max, v));

    // Round to nearest whole or half (0.5 steps)
    v = Math.round(v * 2) / 2;

    this.displayValue = this.formatValue(v);

    this.modelValue = v;
    this.onChange(v);
    this.onTouched();
    this.valueAccepted.emit(v);
  }

  formatValue(v: number): string {
    if (v % 1 === 0) {
      return String(v); // whole number → no decimals
    }
    return v.toFixed(1).replace('.', ','); // 1.5 → "1,5"
  }

  // ControlValueAccessor
  writeValue(obj: any): void {
    this.modelValue = obj;
    if (obj == null) {
      this.displayValue = '';
    } else {
      this.displayValue = this.formatValue(obj);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Validation
  validate() {
    if (this.modelValue == null) return null;
    if (this.modelValue < this.min) return { min: true };
    if (this.modelValue > this.max) return { max: true };
    return null;
  }
}
